import React from "react";
import { Box, keyframes } from "@mui/material";

const drawStroke = keyframes`
  0%, 10% { stroke-dashoffset: 140; opacity: 0.35; }
  42%, 72% { stroke-dashoffset: 0; opacity: 1; }
  100% { stroke-dashoffset: 0; opacity: 0.9; }
`;

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.4,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

function DrawSvg({ delay = 0, size = 44, children, ...rest }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 48 48"
      width={size}
      height={size}
      aria-hidden
      sx={{
        display: "block",
        overflow: "visible",
        "& path, & rect, & circle, & line, & polyline": {
          strokeDasharray: 140,
          animation: `${drawStroke} 4.4s ease-in-out infinite`,
          animationDelay: `${delay}s`,
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
            strokeDashoffset: 0,
            opacity: 1,
          },
        },
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}

/** Chaqueta — croquis de línea, mismo lenguaje stroke que el resto. */
export function IconDesign({ delay, size }) {
  return (
    <DrawSvg delay={delay} size={size}>
      {/* cuello / solapa */}
      <path {...stroke} d="M18 12 L24 17 L30 12" />
      {/* hombros y mangas */}
      <path {...stroke} d="M18 12 L10 18 L13 24 L17 21" />
      <path {...stroke} d="M30 12 L38 18 L35 24 L31 21" />
      {/* cuerpo */}
      <path {...stroke} d="M17 21 V38 H31 V21" />
      {/* cierre */}
      <path {...stroke} d="M24 17 V38" opacity="0.55" />
      {/* bajo */}
      <path {...stroke} d="M17 38 H31" />
    </DrawSvg>
  );
}

export function IconTechPack({ delay, size }) {
  return (
    <DrawSvg delay={delay} size={size}>
      <rect {...stroke} x="12" y="8" width="24" height="32" rx="2" />
      <path {...stroke} d="M18 16 H30 M18 22 H28 M18 28 H26" />
      <path {...stroke} d="M27 31 L31 29 L33 33 L29 35 Z" opacity="0.85" />
    </DrawSvg>
  );
}

export function IconMoodboard({ delay, size }) {
  return (
    <DrawSvg delay={delay} size={size}>
      <rect {...stroke} x="8" y="10" width="16" height="14" rx="1.5" />
      <rect {...stroke} x="26" y="10" width="14" height="10" rx="1.5" />
      <rect {...stroke} x="8" y="28" width="12" height="12" rx="1.5" />
      <rect {...stroke} x="22" y="24" width="18" height="16" rx="1.5" />
    </DrawSvg>
  );
}

export function IconRecap({ delay, size }) {
  return (
    <DrawSvg delay={delay} size={size}>
      <path {...stroke} d="M12 14 H36 V34 H22 L16 40 V34 H12 Z" />
      <path {...stroke} d="M18 22 H30 M18 27 H26" />
    </DrawSvg>
  );
}

/** Gestión (antes logística): tablero / flujo */
export function IconGestion({ delay, size }) {
  return (
    <DrawSvg delay={delay} size={size}>
      <rect {...stroke} x="8" y="10" width="10" height="28" rx="1.5" />
      <rect {...stroke} x="19" y="10" width="10" height="20" rx="1.5" />
      <rect {...stroke} x="30" y="10" width="10" height="24" rx="1.5" />
      <path {...stroke} d="M11 16 H15 M22 16 H26 M33 16 H37" opacity="0.55" />
    </DrawSvg>
  );
}

export function IconProduct({ delay, size }) {
  return (
    <DrawSvg delay={delay} size={size}>
      <path {...stroke} d="M18 12 H30 L34 18 V38 H14 V18 Z" />
      <path {...stroke} d="M18 12 C18 8 30 8 30 12" />
      <path {...stroke} d="M24 18 V26" opacity="0.55" />
    </DrawSvg>
  );
}

export function IconMeetings({ delay, size }) {
  return (
    <DrawSvg delay={delay} size={size}>
      <rect {...stroke} x="10" y="12" width="28" height="22" rx="2" />
      <path {...stroke} d="M16 10 V14 M32 10 V14" />
      <path {...stroke} d="M10 18 H38" />
      <circle {...stroke} cx="20" cy="26" r="2.2" />
      <circle {...stroke} cx="28" cy="26" r="2.2" />
    </DrawSvg>
  );
}

export function IconAiDesigns({ delay, size }) {
  return (
    <DrawSvg delay={delay} size={size}>
      <path
        {...stroke}
        d="M24 8 L27 18 L38 18 L29 24 L32 34 L24 28 L16 34 L19 24 L10 18 L21 18 Z"
      />
    </DrawSvg>
  );
}

export function IconSketches({ delay, size }) {
  return (
    <DrawSvg delay={delay} size={size}>
      <path {...stroke} d="M14 34 L16 18 L24 10 L32 18 L34 34 Z" />
      <path {...stroke} d="M20 34 V28 H28 V34" opacity="0.55" />
      <path {...stroke} d="M18 22 H30" opacity="0.4" />
    </DrawSvg>
  );
}

export function IconSeamless({ delay, size }) {
  return (
    <DrawSvg delay={delay} size={size}>
      <rect {...stroke} x="8" y="8" width="14" height="14" rx="1" />
      <rect {...stroke} x="26" y="8" width="14" height="14" rx="1" />
      <rect {...stroke} x="8" y="26" width="14" height="14" rx="1" />
      <rect {...stroke} x="26" y="26" width="14" height="14" rx="1" />
      <path
        {...stroke}
        d="M15 15 Q24 12 33 15 Q36 24 33 33 Q24 36 15 33 Q12 24 15 15"
        opacity="0.7"
      />
    </DrawSvg>
  );
}

export function IconTartan({ delay, size }) {
  return (
    <DrawSvg delay={delay} size={size}>
      <rect {...stroke} x="10" y="10" width="28" height="28" rx="2" />
      <path {...stroke} d="M18 10 V38 M30 10 V38" opacity="0.55" />
      <path {...stroke} d="M10 18 H38 M10 30 H38" opacity="0.55" />
      <path {...stroke} d="M14 10 V38 M24 10 V38 M34 10 V38" opacity="0.28" />
      <path {...stroke} d="M10 14 H38 M10 24 H38 M10 34 H38" opacity="0.28" />
    </DrawSvg>
  );
}

export function IconClientInfo({ delay, size }) {
  return (
    <DrawSvg delay={delay} size={size}>
      <circle {...stroke} cx="24" cy="16" r="6" />
      <path {...stroke} d="M12 38 C12 28 36 28 36 38" />
      <path {...stroke} d="M32 12 H38 V18" opacity="0.55" />
    </DrawSvg>
  );
}

/** @deprecated alias */
export const IconLogistics = IconGestion;
